// apple-sign-in.mjs - JavaScript module for Apple Sign In operations in Kotlin/Wasm

/**
 * JsResult class for handling success and error states in Kotlin/WASM interop
 */
class JsResult {
    constructor(data = null, error = null) {
        this.data = data;
        this.error = error;
    }

    static success(data) {
        return new JsResult(data, null);
    }

    static error(errorMessage) {
        return new JsResult(null, errorMessage);
    }

    get isSuccess() {
        return this.error === null;
    }

    get isError() {
        return this.error !== null;
    }
}

/**
 * Initialize Apple Sign In
 * @param {string} clientId - Your Apple Services ID
 * @param {string} redirectURI - Redirect URI for callbacks
 * @param {string} scope - Space-separated scopes (e.g., "name email")
 * @param {boolean} usePopup - Whether to use popup (true) or redirect (false)
 * @returns {JsResult} Success or error result
 */
export function initAppleSignIn(clientId, redirectURI, scope = "name email", usePopup = true) {
    console.log("[JS] initAppleSignIn called with clientId:", clientId, "redirectURI:", redirectURI);

    if (clientId == null) {
        console.warn("[JS] initAppleSignIn: clientId is null/undefined");
        return JsResult.error("clientId is required");
    }

    if (redirectURI == null) {
        console.warn("[JS] initAppleSignIn: redirectURI is null/undefined");
        return JsResult.error("redirectURI is required");
    }

    if (typeof AppleID === 'undefined' || AppleID == null) {
        console.warn("[JS] initAppleSignIn: AppleID SDK not loaded");
        return JsResult.error("Apple Sign In SDK not loaded");
    }

    try {
        AppleID.auth.init({
            clientId: clientId,
            scope: scope,
            redirectURI: redirectURI,
            usePopup: usePopup
        });

        console.log("[JS] Apple Sign In initialized successfully");
        return JsResult.success(true);
    } catch (error) {
        console.warn("[JS] initAppleSignIn error:", error);
        return JsResult.error(`Failed to initialize Apple Sign In: ${error.message || error}`);
    }
}

/**
 * Sign in with Apple
 * This returns a Promise that resolves when Apple's auth flow completes
 * Apple uses postMessage (web_message) to send the response
 *
 * @returns {Promise<JsResult>} Promise resolving to authorization data or error
 */
export async function signInWithApple() {
    console.log("[JS] signInWithApple called");

    if (typeof AppleID === 'undefined' || AppleID == null) {
        console.warn("[JS] signInWithApple: AppleID SDK not loaded");
        return JsResult.error("Apple Sign In SDK not loaded");
    }

    if (typeof AppleID.auth === 'undefined' || AppleID.auth == null) {
        console.warn("[JS] signInWithApple: AppleID.auth not available");
        return JsResult.error("Apple Sign In not initialized");
    }

    if (typeof AppleID.auth.signIn !== 'function') {
        console.warn("[JS] signInWithApple: AppleID.auth.signIn not a function");
        return JsResult.error("Apple Sign In method not available");
    }

    try {
        console.log("[JS] Calling AppleID.auth.signIn()");

        // Apple's signIn() returns a Promise that resolves with the authorization data
        // When using web_message mode, Apple sends the data via postMessage and
        // their SDK handles it internally, then resolves the Promise
        const data = await AppleID.auth.signIn();

        console.log("[JS] Apple Sign In successful, received data:", data);

        // Extract authorization data
        // The response structure from Apple includes:
        // - authorization.code (grant_code)
        // - authorization.id_token
        // - authorization.scope
        // - user (only on first sign-in)
        const authData = {
            code: data.authorization?.grant_code || data.authorization?.code,
            idToken: data.authorization?.id_token,
            scope: data.authorization?.scope,
            user: data.user ? {
                name: data.user.name ? {
                    firstName: data.user.name.firstName,
                    lastName: data.user.name.lastName
                } : null,
                email: data.user.email
            } : null,
            // Also include the raw authorizedData if present
            userId: data.authorizedData?.userId
        };

        console.log("[JS] Extracted auth data:");
        console.log("[JS]   - code present:", !!authData.code);
        console.log("[JS]   - idToken present:", !!authData.idToken);
        console.log("[JS]   - user present:", !!authData.user);
        console.log("[JS]   - userId:", authData.userId);

        return JsResult.success(authData);

    } catch (error) {
        console.warn("[JS] signInWithApple error:", error);

        // Handle user cancellation
        if (error.error === 'popup_closed_by_user') {
            return JsResult.error("Sign in cancelled by user");
        }

        // Handle popup blocked
        if (error.error === 'popup_blocked_by_browser') {
            return JsResult.error("Popup blocked by browser. Please allow popups for this site.");
        }

        return JsResult.error(`Apple Sign In failed: ${error.error || error.message || error}`);
    }
}

/**
 * Check sessionStorage for auth code (redirect flow)
 * This is only used if NOT using popup/web_message mode
 *
 * @returns {JsResult} Success with code or error
 */
export function checkStoredAuthCode() {
    console.log("[JS] Checking for stored auth code");

    const code = sessionStorage.getItem('apple_auth_code');
    const idToken = sessionStorage.getItem('apple_id_token');
    const error = sessionStorage.getItem('apple_auth_error');

    if (code || idToken) {
        console.log("[JS] Found stored auth data");
        sessionStorage.removeItem('apple_auth_code');
        sessionStorage.removeItem('apple_id_token');
        return JsResult.success({
            code: code,
            idToken: idToken
        });
    }

    if (error) {
        console.log("[JS] Found stored auth error");
        sessionStorage.removeItem('apple_auth_error');
        return JsResult.error(error);
    }

    console.log("[JS] No stored auth data found");
    return JsResult.success(null);
}

/**
 * Apple Sign In object with all Apple-related functions
 */
const AppleSignInObj = {
    initAppleSignIn: function (clientId, redirectURI, scope, usePopup) {
        console.log("[JS] AppleSignInObj.initAppleSignIn called");
        return initAppleSignIn(clientId, redirectURI, scope, usePopup);
    },

    signInWithApple: async function () {
        console.log("[JS] AppleSignInObj.signInWithApple called");
        return await signInWithApple();
    },

    checkStoredAuthCode: function () {
        console.log("[JS] AppleSignInObj.checkStoredAuthCode called");
        return checkStoredAuthCode();
    }
};

// Make available globally
globalThis.AppleSignInObj = AppleSignInObj;

// Export
export {AppleSignInObj, JsResult};
